import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { easeOut } from "./Shared";

export default function LegalShell({ title, updated, intro, sections, testId }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid={testId} className="min-h-screen bg-[#F4F3EF]">
      <header className="border-b border-black/15">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid={`${testId}-logo`}
            className="font-serif text-base md:text-lg tracking-tight hover:text-[#D4512D] transition-colors duration-300"
          >
            I PUTU ADI SEMARADA LLC
          </Link>
          <Link
            to="/"
            data-testid={`${testId}-back-link`}
            className="link-sweep inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-black/60 hover:text-black transition-colors duration-300 shrink-0"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4512D]"
        >
          Last updated · {updated}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: easeOut }}
          data-testid={`${testId}-title`}
          className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight"
        >
          {title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: easeOut }}
          className="mt-8 max-w-3xl text-base lg:text-lg leading-relaxed text-black/60 font-light"
        >
          {intro}
        </motion.p>

        <div className="mt-16">
          {sections.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, ease: easeOut }}
              className="py-10 border-t border-black/15 last:border-b"
              data-testid={`${testId}-section-${i + 1}`}
            >
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight">{s.h}</h2>
              {s.body?.map((p) => (
                <p key={p.slice(0, 32)} className="mt-5 text-base leading-relaxed text-black/60 font-light">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-5 space-y-3">
                  {s.list.map((li) => (
                    <li key={li.slice(0, 32)} className="flex items-start gap-3 text-base leading-relaxed text-black/60 font-light">
                      <span className="mt-[9px] inline-block w-1.5 h-1.5 bg-[#D4512D] shrink-0" aria-hidden="true" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after && <p className="mt-5 text-base leading-relaxed text-black/60 font-light">{s.after}</p>}
            </motion.section>
          ))}
        </div>
      </main>

      <footer className="border-t border-black/15">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black/45">
          <span>© 2026 I Putu Adi Semarada LLC</span>
          <span>Registered in Wyoming, USA</span>
        </div>
      </footer>
    </div>
  );
}
